/**
 * WorkspaceCenter - Merkez panel
 *
 * Icerik:
 * - Proje basligi + metadata
 * - Mevcut cycle icerigi (Wikipedia stili)
 * - Onceki cycle ozetleri (accordion)
 * - Kaynaklar bolumu
 * - Katkida bulunanlar
 */

import { ChevronDown, ChevronUp, FileText, Users as UsersIcon } from 'lucide-react'
import { useState } from 'react'

interface Project {
  id: number
  title: string
  problem_statement: string
  current_cycle: number
  living_summary: string
  status: string
}

interface WorkspaceCenterProps {
  project: Project
}

export default function WorkspaceCenter({ project }: WorkspaceCenterProps) {
  const [expandedCycles, setExpandedCycles] = useState<number[]>([])

  const toggleCycle = (cycleNum: number) => {
    setExpandedCycles(prev =>
      prev.includes(cycleNum)
        ? prev.filter(c => c !== cycleNum)
        : [...prev, cycleNum]
    )
  }

  // Ornek onceki cycle'lar (gercekte API'den gelecek)
  const previousCycles = Array.from({ length: project.current_cycle - 1 }, (_, i) => ({
    cycle: i + 1,
    title: `Cycle ${i + 1}`,
    summary: `Cycle ${i + 1} ozeti...`,
    completed_at: new Date().toISOString()
  }))

  return (
    <div className="space-y-5">
      {/* Proje Basligi */}
      <div className="card">
        <div className="card-body">
          <h1 className="text-3xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {project.title}
          </h1>

          <div className="flex items-center gap-4 mb-4">
            <span className={`badge ${
              project.status === 'active' ? 'badge-success' :
              project.status === 'paused' ? 'badge-warning' :
              project.status === 'completed' ? 'badge-info' :
              'badge-secondary'
            }`}>
              {project.status}
            </span>
            <span style={{ color: 'var(--text-secondary)' }}>
              Cycle {project.current_cycle}
            </span>
          </div>

          <div
            className="p-4 rounded"
            style={{ background: 'var(--card-bg-secondary)' }}
          >
            <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Problem Tanimi
            </h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              {project.problem_statement}
            </p>
          </div>
        </div>
      </div>

      {/* Mevcut Cycle Icerigi */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2">
            <FileText size={20} />
            Cycle {project.current_cycle} - Mevcut Calisma
          </h2>
        </div>
        <div className="card-body">
          {project.living_summary ? (
            <div
              className="prose max-w-none"
              style={{ color: 'var(--text-primary)' }}
            >
              <p>{project.living_summary}</p>
            </div>
          ) : (
            <p style={{ color: 'var(--text-secondary)' }}>
              Bu cycle henuz baslamadi veya icerik uretilmedi.
            </p>
          )}
        </div>
      </div>

      {/* Onceki Cycle'lar (Accordion) */}
      {previousCycles.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Onceki Cycle&apos;lar</h2>
          </div>
          <div className="card-body space-y-2">
            {previousCycles.map((cycle) => (
              <div
                key={cycle.cycle}
                className="border rounded"
                style={{ borderColor: 'var(--border-color)' }}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleCycle(cycle.cycle)}
                  className="w-full p-4 flex items-center justify-between hover:bg-opacity-50 transition"
                  style={{ background: 'var(--card-bg-secondary)' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {cycle.title}
                    </span>
                    <span className="badge badge-success">Tamamlandi</span>
                  </div>
                  {expandedCycles.includes(cycle.cycle) ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </button>

                {/* Accordion Content */}
                {expandedCycles.includes(cycle.cycle) && (
                  <div className="p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {cycle.summary}
                    </p>
                    <p className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>
                      Tamamlanma: {new Date(cycle.completed_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kaynaklar */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2">
            <FileText size={20} />
            Kaynaklar
          </h2>
        </div>
        <div className="card-body">
          <p style={{ color: 'var(--text-secondary)' }}>
            Henuz kaynak eklenmedi.
          </p>
        </div>
      </div>

      {/* Katkida Bulunanlar */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center gap-2">
            <UsersIcon size={20} />
            Katkida Bulunanlar
          </h2>
        </div>
        <div className="card-body">
          <p style={{ color: 'var(--text-secondary)' }}>
            Henuz katkida bulunan yok.
          </p>
        </div>
      </div>
    </div>
  )
}
